import React from 'react';

import { BlockArticlePreview as BlockArticle } from '@lumapps/widget-article-list/components/BlockArticlePreview';
import { BlockCommunityPreview } from '@lumapps/widget-community-list/components/BlockCommunityPreview';
import { BlockPagePreview } from '@lumapps/widget-content-list/components/BlockPagePreview';
import { BlockStructuredContent } from '@lumapps/widget-contribution/components/BlockStructuredContent';
import { BlockDirectoryEntry } from '@lumapps/widget-directory-entries/components/BlockDirectoryEntry';
import { BlockEventPreview } from '@lumapps/widget-event-list/components/BlockEventPreview';
import { BlockEventMetadata } from '@lumapps/widget-event-metadata/components/BlockEventMetadata';
import { BlockFeaturedImage } from '@lumapps/widget-featured-image/components/BlockFeaturedImage';
import { BlockHtml } from '@lumapps/widget-html/components/BlockHtml';
import { BlockImagePreview } from '@lumapps/widget-image-gallery/components/BlockImagePreview';
import { BlockIntro } from '@lumapps/widget-intro/components/BlockIntro';
import { BlockPersonalFeedPreview } from '@lumapps/widget-personal-feed-preview/components/BlockPersonalFeedPreview';
import { BlockPlayVideo } from '@lumapps/widget-play/components/BlockPlayVideo';
import { BlockPlayVideoPlaylist } from '@lumapps/widget-playlist/components/BlockPlayVideoPlaylist';
import { BlockPost } from '@lumapps/widget-post-list/components/BlockPost';
import { BlockLearningPreview } from '@lumapps/widget-recommended-training-courses/components/BlockLearningPreview';
import { BlockRemote } from '@lumapps/widget-remote/components/BlockRemote';
import { BlockResourceCreationInfo } from '@lumapps/widget-resource-metadata/components/BlockResourceCreationInfo';
import { BlockStreamItem } from '@lumapps/widget-stream/components/BlockStreamItem';
import { BlockSummary } from '@lumapps/widget-summary/components/BlockSummary/BlockSummary';
import { BlockTitle } from '@lumapps/widget-title/components/BlockTitle';
import { BlockTraining } from '@lumapps/widget-training-list/components/BlockTraining';
import { BlockUserProfilePreview } from '@lumapps/widget-user-list/components/BlockUserProfilePreview';

import { Blocks } from '../../types';
import { BlockAugmentedContainer } from './BlockAugmentedContainer';
import { BlockCascade } from './BlockCascade';
import { BlockClientComputed } from './BlockClientComputed';
import { BlockGrid } from './BlockGrid';
import { BlockList } from './BlockList';
import { BlockNoResults } from './BlockNoResults';
import { BlockReactions } from './BlockReactions';
import { BlockSlideshow } from './BlockSlideshow';
import { BlockTabFilteredContainer } from './BlockTabFilteredContainer';
import { BlockUser } from './BlockUser';
import { BlockUserList } from './BlockUserList';

/** Blocks for basic content display. */
const CONTENT_BLOCKS = {
    BlockNoResults,
    BlockHtml,
    BlockTitle,
    BlockIntro,
    BlockFeaturedImage,
    BlockSummary,
    BlockStructuredContent,
} as const;

/** Blocks for layout and containing other blocks. */
const CONTAINER_BLOCKS = {
    BlockList,
    BlockTabFilteredContainer,
    BlockSlideshow,
    BlockGrid,
    BlockCascade,
    BlockClientComputed,
    BlockAugmentedContainer,
} as const;

/** Blocks shared across multiple resource types. */
const SHARED_BLOCKS = {
    BlockUser,
    BlockResourceCreationInfo,
    BlockImagePreview,
    BlockUserList,
    BlockReactions,
} as const;

/** Blocks for specific resource types. */
const RESOURCE_BLOCKS = {
    BlockDirectoryEntry,
    BlockPagePreview,
    BlockCommunityPreview,
    BlockUserProfilePreview,
    BlockStreamItem,
    BlockPersonalFeedPreview,
    BlockLearningPreview,
    BlockTraining,
    BlockEventMetadata,
    BlockPost,
    BlockRemote,
    BlockEventPreview,
    BlockArticle,
    BlockPlayVideo,
    BlockPlayVideoPlaylist,
} as const;

/**
 * Index of all possible block renderers by block type.
 *
 * @see `Blocks` for the union type of all possible blocks.
 */
export const BLOCK_RENDERERS: { [blockType in Blocks['type']]: React.FC<any> } = {
    ...CONTENT_BLOCKS,
    ...CONTAINER_BLOCKS,
    ...SHARED_BLOCKS,
    ...RESOURCE_BLOCKS,
};
